import {
	FC, useCallback, useEffect, useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useCanViewPermission } from '@copilot/common/hooks/permission';
import { PermissionName } from '@copilot/common/hooks/permission/interface';
import { ModelState } from '@copilot/common/store/models/fetch';
import { OrganizationMemberSelectors } from '@copilot/common/store/selectors/organizationMember';
import { CampaignMemberModel, getName } from '@copilot/common/utils/campaignMember/models';
import { selectCampaignMembers } from '@copilot/common/utils/campaignMember/selector';
import {
	ICampaignMemberOptionsHelperProps, ICampaignDashboardSearchListBaseProps, ICampaignDashboardSearchListProps, ICampaignMemberOptionsProps, ICampaignMemberSelectProps,
} from './const';
import { updateSearchListSelectedMemberId, resetOneCampaignSearchListTab } from './data/actions';
import { campaignSearchListTabMemberSelector } from './data/selector';
import { useOnUnmount } from '@copilot/common/hooks/common';
import { IDropdownOptions } from '@copilot/common/components/dropdownWithTitle';
import { LoadingPane } from '@copilot/common/components/loadingPane/loadingPane';
import * as _ from 'lodash';

/**
 * HOC that interacts with the CampaignSearchListTab in the redux store
 * @param {FC} Component the component to apply the HOC to
 */
export const withSelectedCampaignMember = <T extends ICampaignDashboardSearchListBaseProps>(Component: FC<T & ICampaignMemberSelectProps & ICampaignMemberOptionsHelperProps>) => (props: T) => {
	const { campaignId } = props;
	const dispatch = useDispatch();
	const isCampaignMemberOptionsVisible = useCanViewPermission(PermissionName.SearchList);

	// bind campaignId to the actions
	const updateSelectedMemberIdInCampaign = _.partial(updateSearchListSelectedMemberId, campaignId);
	const resetCampaignSearchListTabInCampaign = _.partial(resetOneCampaignSearchListTab, campaignId);

	// calls the action to update CampaignSearchListTab in the redux store
	const updateSelectedMemberId = useCallback((id: string | undefined) => dispatch(updateSelectedMemberIdInCampaign(id)), []);

	// Note: memoizing the selector prevents redundant calls to the selector
	const selectedMemberSelector = useMemo(() => campaignSearchListTabMemberSelector(campaignId), [campaignId]);
	const campaignMembersSelector = useMemo(() => selectCampaignMembers(campaignId), [campaignId]);
	const selectedCampaignMember: CampaignMemberModel | undefined = useSelector(selectedMemberSelector);
	const campaignMembers: ModelState<CampaignMemberModel> = useSelector(campaignMembersSelector);

	/**
	 * Resets the campaign search list tab object when leaving the page
	 */
	 useOnUnmount(() => {
		dispatch(resetCampaignSearchListTabInCampaign());
	});

	return (
		<LoadingPane isLoading={(isCampaignMemberOptionsVisible === undefined) || (campaignMembers.loading)}>
			<Component
				{...props}
				campaignMembers={campaignMembers}
				isCampaignMemberOptionsVisible={isCampaignMemberOptionsVisible}
				campaignMember={selectedCampaignMember}
				onCampaignMemberSelect={updateSelectedMemberId}
			/>
		</LoadingPane>
	);
};

/**
 * HOC that provides campaign member options from the given campaign members
 * @param {FC} Component the component to apply the HOC to
 */
export const withCampaignMemberOptions = <T extends ICampaignDashboardSearchListBaseProps & ICampaignMemberSelectProps & ICampaignMemberOptionsHelperProps>(Component: FC<T & ICampaignMemberOptionsProps>) => (props: T) => {
	const { campaignMembers, isCampaignMemberOptionsVisible } = props;

	const campaignMemberOptions: IDropdownOptions[] | undefined = useMemo(() => {
		if (isCampaignMemberOptionsVisible) {
			return campaignMembers?.data.map((m) => ({ id: m.id, label: getName(m) }));
		} else return undefined;
	}, [isCampaignMemberOptionsVisible, campaignMembers?.data]);

	return (
		<Component
			{...props}
			campaignMemberOptions={campaignMemberOptions}
		/>
	);
};

/**
 * HOC that sets the initial selectedCampaignMemberId, if there is one selected as default
 * @param {FC} Component the component to apply the HOC to
 */
export const withInitialCampaignMember = <T extends ICampaignDashboardSearchListProps & ICampaignMemberOptionsHelperProps>(Component: FC<T>) => (props: T) => {
	const {
		campaignMembers, isCampaignMemberOptionsVisible, initialSelectedMemberId, onCampaignMemberSelect,
	} = props;
	const activeMember = useSelector(OrganizationMemberSelectors.getActiveMember);

	useEffect(() => {
		if (!isCampaignMemberOptionsVisible) {
			const campaignMemberId = campaignMembers?.data.find((m) => m.orgMemberId === activeMember?.id)?.id;
			onCampaignMemberSelect(campaignMemberId);
		} else if (campaignMembers?.data.length === 1) {
			onCampaignMemberSelect(campaignMembers.data[0].id);
		} else {
			onCampaignMemberSelect(initialSelectedMemberId);
		}
	}, [initialSelectedMemberId]); // adding onCampaignMemberSelect and campaignMembers to dependency causes issues after redirect

	return (
		<Component {...props} />
	);
};
